import {announce} from '@github-ui/aria-live'
import {Banner, Button} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {type FC, useCallback, useMemo} from 'react'
import {getDisplayFilePaths, getFindingApplicationKey, sortFindingsBySeverity} from './entity-helpers'
import {Finding} from './Finding'
import type {FindingResolvedReason, Finding as FindingType} from './types'
import type {FindingsView} from './use-filter-findings'

interface FindingsListProps {
  displayedFindings: FindingType[]
  selectedView: FindingsView
  attentionCount: number
  otherCount: number
  ignoredCount: number
  outdatedCount: number
  expandedFindingIds: ReadonlySet<string>
  fixCcaTaskIds: Record<string, string>
  pullRequestBasePath: string
  onViewChange: (view: FindingsView) => void
  onToggleExpanded: (findingId: string) => void
  onDismiss: (finding: FindingType, resolvedReason: FindingResolvedReason) => void
  onReopen: (finding: FindingType) => void
  onFix: (finding: FindingType) => void
  sweAgentEnabled: boolean
  dotcomChatEnabled: boolean
  headSha: string | null
  canApplySuggestion: boolean
  appliedFindingVersions: ReadonlyMap<string, string>
  onSuggestionApplied: (finding: FindingType) => void
}

export const FindingsList: FC<FindingsListProps> = ({
  displayedFindings,
  selectedView,
  attentionCount,
  otherCount,
  ignoredCount,
  outdatedCount,
  expandedFindingIds,
  fixCcaTaskIds,
  pullRequestBasePath,
  onViewChange,
  onToggleExpanded,
  onDismiss,
  onReopen,
  onFix,
  sweAgentEnabled,
  dotcomChatEnabled,
  headSha,
  canApplySuggestion,
  appliedFindingVersions,
  onSuggestionApplied,
}) => {
  const sorted = useMemo(() => sortFindingsBySeverity(displayedFindings), [displayedFindings])
  const displayFilePaths = useMemo(
    () => getDisplayFilePaths(sorted.filter(finding => finding.fileUrl).map(finding => finding.filePath)),
    [sorted],
  )

  const resolved = selectedView === 'resolved'
  const outdated = selectedView === 'outdated'
  const taskBasePath = pullRequestBasePath.replace(/\/pull\/.*$/, '')
  const feedbackPath = `${pullRequestBasePath}/code_review_findings/feedback`
  const emptyState = getEmptyState(selectedView, {attentionCount, otherCount, ignoredCount, outdatedCount})
  const handleEmptyStateViewChange = useCallback(
    (view: FindingsView) => {
      onViewChange(view)
      requestAnimationFrame(() => {
        document.getElementById('findings-heading')?.focus({preventScroll: true})
        announce(getViewSwitchAnnouncement(view, {attentionCount, otherCount, ignoredCount, outdatedCount}))
      })
    },
    [attentionCount, ignoredCount, onViewChange, otherCount, outdatedCount],
  )

  if (displayedFindings.length === 0) {
    const emptyStateAction = emptyState.action

    return (
      <>
        <div role="status" className="sr-only">
          No findings displayed
        </div>
        <Blankslate className="border rounded-3">
          <Blankslate.Heading>{emptyState.heading}</Blankslate.Heading>
          <Blankslate.Description>{emptyState.description}</Blankslate.Description>

          {emptyStateAction && (
            <div className="d-flex flex-items-center tmp-mt-3">
              <Button onClick={() => handleEmptyStateViewChange(emptyStateAction.view)}>
                {emptyStateAction.label}
              </Button>
            </div>
          )}
        </Blankslate>
      </>
    )
  }

  return (
    <>
      {outdated && (
        <Banner hideTitle variant="warning" layout="compact" className="tmp-mb-3" title="Outdated findings">
          These findings are from previous commits and may no longer be applicable
        </Banner>
      )}
      <div role="status" className="sr-only">
        {`Showing ${sorted.length} ${sorted.length === 1 ? 'finding' : 'findings'}`}
      </div>
      <ul aria-label="Findings">
        {sorted.map(finding => (
          <Finding
            key={finding.id}
            finding={finding}
            displayFilePath={displayFilePaths.get(finding.filePath)}
            resolved={resolved}
            isExpanded={expandedFindingIds.has(finding.id)}
            ccaTaskId={fixCcaTaskIds[finding.id] ?? finding.ccaTaskId ?? undefined}
            feedbackPath={feedbackPath}
            taskBasePath={taskBasePath}
            pullRequestBasePath={pullRequestBasePath}
            onToggleExpanded={() => onToggleExpanded(finding.id)}
            onDismiss={resolvedReason => onDismiss(finding, resolvedReason)}
            onReopen={() => onReopen(finding)}
            onFix={() => onFix(finding)}
            sweAgentEnabled={sweAgentEnabled}
            dotcomChatEnabled={dotcomChatEnabled}
            applyHeadSha={
              canApplySuggestion && !resolved && !outdated && headSha && finding.suggestion.trim().length > 0
                ? headSha
                : undefined
            }
            suggestionApplied={
              headSha !== null && appliedFindingVersions.get(finding.id) === getFindingApplicationKey(finding, headSha)
            }
            onSuggestionApplied={() => onSuggestionApplied(finding)}
          />
        ))}
      </ul>
    </>
  )
}

interface EmptyStateCounts {
  attentionCount: number
  otherCount: number
  ignoredCount: number
  outdatedCount: number
}

const getEmptyState = (selectedView: FindingsView, counts: EmptyStateCounts) => {
  const action = getFirstAvailableViewAction(
    [
      ['attention', counts.attentionCount, 'View higher-severity findings'],
      ['open', counts.otherCount, 'View other findings'],
      ['resolved', counts.ignoredCount, 'View resolved findings'],
      ['outdated', counts.outdatedCount, 'View outdated findings'],
    ],
    selectedView,
  )

  if (selectedView === 'attention') {
    return {
      heading: 'No findings need your attention',
      description: 'There are no higher-severity findings for this pull request.',
      action,
    }
  }

  if (selectedView === 'open') {
    return {
      heading: 'No other findings',
      description: 'There are no lower-severity findings for this pull request.',
      action,
    }
  }

  if (selectedView === 'outdated') {
    return {
      heading: 'No outdated findings',
      description: 'There are no outdated findings from previous commits.',
      action,
    }
  }

  return {
    heading: 'No resolved findings',
    description: 'There are no resolved findings for this pull request.',
    action,
  }
}

const getFirstAvailableViewAction = (
  options: Array<[view: FindingsView, count: number, label: string]>,
  selectedView: FindingsView,
) => {
  const option = options.find(([view, count]) => view !== selectedView && count > 0)
  if (!option) return null

  const [view, , label] = option
  return {view, label}
}

const pluralizeFindings = (count: number, modifier?: string): string =>
  `${count} ${modifier ? `${modifier} ` : ''}${count === 1 ? 'finding' : 'findings'}`

const getViewSwitchAnnouncement = (view: FindingsView, counts: EmptyStateCounts): string => {
  if (view === 'attention') return `Showing ${pluralizeFindings(counts.attentionCount)} needing attention`
  if (view === 'open') return `Showing ${pluralizeFindings(counts.otherCount, 'lower-severity')}`
  if (view === 'outdated') return `Showing ${pluralizeFindings(counts.outdatedCount, 'outdated')}`
  return `Showing ${pluralizeFindings(counts.ignoredCount)} resolved`
}
